import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Dog, Pill, HeartPulse, GraduationCap, Users, Sparkles, ArrowRight, PawPrint, Bell, Crown } from "lucide-react";
import { AdBanner } from "@/components/AdBanner";

const StatCard = ({ icon: Icon, label, value, tone, delay }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
    <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="text-3xl font-head font-bold tracking-tight" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</div>
    <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
  </motion.div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data));
    api.get("/dogs").then((r) => setDogs(r.data));
    api.get("/reminders").then((r) => setReminders(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <AdBanner placement="dashboard" />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight">Welcome back 🐾</h1>
          <p className="text-muted-foreground mt-2 text-base">Your pack's health, behaviour and training at a glance.</p>
        </div>
        <Link to="/dogs" data-testid="add-dog-cta" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors">
          <PawPrint className="h-4 w-4" /> Manage dogs
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
        <StatCard icon={Dog} label="Dogs" value={stats?.dogs ?? "–"} tone="bg-primary/15 text-primary" delay={0.02} />
        <StatCard icon={Pill} label="Active Meds" value={stats?.active_medications ?? "–"} tone="bg-secondary/15 text-secondary" delay={0.06} />
        <StatCard icon={HeartPulse} label="Health Issues" value={stats?.ongoing_health ?? "–"} tone="bg-destructive/15 text-destructive" delay={0.1} />
        <StatCard icon={GraduationCap} label="Training Logs" value={stats?.training_sessions ?? "–"} tone="bg-accent/20 text-accent-foreground" delay={0.14} />
        <StatCard icon={Users} label="Behaviour Logs" value={stats?.behaviour_logs ?? "–"} tone="bg-primary/15 text-primary" delay={0.18} />
        <StatCard icon={Sparkles} label="AI Researches" value={stats?.ai_researches ?? "–"} tone="bg-accent/20 text-accent-foreground" delay={0.22} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head text-lg font-semibold">Your Dogs</h2>
            <Link to="/dogs" className="text-sm text-primary font-medium flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {dogs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center bg-card/50">
              <Dog className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No dogs yet. Add your first dog to start tracking.</p>
              <Link to="/dogs" data-testid="empty-add-dog" className="inline-block mt-4 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold">Add a dog</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {dogs.map((d) => (
                <Link to={`/dogs/${d.id}`} key={d.id} data-testid={`dog-card-${d.id}`}
                  className="rounded-2xl bg-card border border-border/70 p-4 flex items-center gap-4 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                  <div className="h-14 w-14 rounded-2xl overflow-hidden bg-muted shrink-0">
                    {d.photo_url ? <img src={d.photo_url} alt={d.name} className="h-full w-full object-cover" />
                      : <div className="h-full w-full flex items-center justify-center"><PawPrint className="h-6 w-6 text-muted-foreground" /></div>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-head font-semibold truncate">{d.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{d.breed || "Mixed"} {d.pack_role ? `· ${d.pack_role}` : ""}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl bg-card border border-border/70 p-5 shadow-sm" data-testid="reminders-card">
            <div className="flex items-center gap-2 mb-3"><Bell className="h-5 w-5 text-secondary" /><h3 className="font-head font-semibold">Medication reminders</h3></div>
            {reminders.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active medications. Add a schedule from a dog's Health & Meds tab.</p>
            ) : (
              <div className="space-y-2">
                {reminders.map((r) => (
                  <div key={r.id} data-testid={`reminder-${r.id}`} className="flex items-center gap-3 rounded-xl bg-secondary/5 border border-secondary/20 px-3 py-2">
                    <Pill className="h-4 w-4 text-secondary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{r.name} <span className="text-muted-foreground font-normal">{r.dosage}</span></div>
                      <div className="text-xs text-muted-foreground">{r.dog_name}{r.frequency ? ` · ${r.frequency}` : ""}{r.time_of_day ? ` · ${r.time_of_day}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-gradient-to-br from-accent/20 to-primary/10 border border-accent/30 p-6">
            <Sparkles className="h-6 w-6 text-accent-foreground mb-3" />
            <h3 className="font-head text-lg font-semibold mb-1">Not sure what's wrong?</h3>
            <p className="text-sm text-muted-foreground mb-4">Describe symptoms or behaviour and let AI research possible causes across vet sources & forums.</p>
            <div className="flex flex-col gap-2">
              <Link to="/research" data-testid="quick-research" className="rounded-xl bg-card border border-border px-4 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">🩺 Research health symptoms</Link>
              <Link to="/behaviour" data-testid="quick-behaviour" className="rounded-xl bg-card border border-border px-4 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">🐕 Analyse pack behaviour</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
